import { Navigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../context/app-context.tsx";
import { useBodyLayout } from "../hooks/use-body-layout.ts";
import { getMainAppUrl } from "../lib/app-urls.ts";
import FullPageLoader from "../components/full-page-loader.tsx";

type Reason = 'no_session' | 'permission_denied';

function readReason(raw: string | null): Reason {
    return raw === 'permission_denied' ? 'permission_denied' : 'no_session';
}

/**
 * The dead end for anyone the app cannot let in. It never re-checks the session
 * itself — AppProvider already resolved it, and asking again only produced a
 * second `/user` request and a redirect ping-pong with AuthGate.
 *
 * Both exits lead to the main app: this app has no login of its own, so "/" is
 * never a way out — it just bounces straight back here.
 */
export default function Unauthorized() {
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const { authStatus } = useAppContext();

    useBodyLayout('plain');

    const reason = readReason(searchParams.get('reason'));
    const mainAppUrl = getMainAppUrl();

    if (authStatus === 'validating') {
        return <FullPageLoader/>;
    }

    // Landed here with a working session (stale link, bookmark) — go home.
    if (authStatus === 'authenticated') {
        return <Navigate to="/" replace/>;
    }

    const content = reason === 'permission_denied'
        ? {
            icon: 'icon-stop',
            title: t('unauthorized.permission_denied.title', 'Insufficient permissions'),
            message: t(
                'unauthorized.permission_denied.message',
                'You do not have the required permissions to access this network application. Please contact your administrator to request the "manage_network" permission.'
            ),
        }
        : {
            icon: 'icon-user_profile',
            title: t('unauthorized.no_session.title', 'Your session has ended'),
            message: t(
                'unauthorized.no_session.message',
                'Sign in to Zooza and open the network application from there.'
            ),
        };

    return (
        <div className="message_screen">
            <div className="message_screen_card">
                <em className={`${content.icon} message_screen_icon`} aria-hidden="true"></em>
                <h1>{content.title}</h1>
                <p>{content.message}</p>
                <div className="message_screen_actions">
                    <a href={mainAppUrl} role="button" className="z2 primary">
                        {t('unauthorized.back_to_main', 'Back to Main App')}
                    </a>
                </div>
            </div>
        </div>
    );
}
